import React from 'react';
import { send } from '../assets/images/image';
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from 'react-toastify';
import ContactMobile from '../layouts/mobile/ContactMobile';
import { motion } from "framer-motion";
import { API_URL, RECAPTCHA_KEY } from '../configs/default.config';

const fadeUp = {
    hidden: { y: 20, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
            delayChildren: 0.3,
        }
    }
};

const ContactForm: React.FC = () => {
    const recaptchaRef = React.useRef<ReCAPTCHA | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [captchaToken, setCaptchaToken] = React.useState<string | null>(null);
    const [formData, setFormData] = React.useState({
        name: '',
        email: '',
        message: '',
    });

    const handleSubmtit = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.name || !formData.email || !formData.message) {
            toast.error("Please fill out all required fields.");
            return;
        }

        if (!emailRegex.test(formData.email)) {
            toast.error("Invalid email format.");
            return;
        }

        if (!captchaToken) {
            toast.error("Please complete the CAPTCHA.");
            return;
        }
        setLoading(true);
        try {
            const response = await fetch(API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...formData,
                    recaptchaToken: captchaToken,
                }),
            });
            if (!response.ok) throw new Error("Failed to send message.");
            toast.success("Message sent successfully");
            setFormData({
                name: '',
                email: '',
                message: '',
            });
            recaptchaRef.current?.reset();
            setCaptchaToken(null);
            setLoading(false);
        } catch (error) {
            toast.error("Something went wrong. Please try again.");
            setLoading(false);
        }
    };

    return (
        <>
            <div className="block md:hidden mt-[100px]">
                <ContactMobile />
            </div>

            <div className="hidden md:block">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={0}
                    className="flex flex-row w-full text-center text-[65px] font-[274] text-[#494949] mt-[60px] item-center justify-center space-x-5"
                >
                    <div className='inline-flex h-[70px] justify-center items-center gap-4 shrink-0 pb-1'>
                        We're Here
                    </div>
                    <div className='inline-flex h-[70px] px-2 justify-center items-center gap-4 shrink-0 bg-[#3A57FF] text-[#FAFAFA] text-[60px] font-[1000] pb-1'>
                        to Help
                    </div>
                </motion.div>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    custom={1}
                    className='flex flex-col mt-[32.5px]'
                >
                    <div className="flex flex-row justify-center gap-8 space-x-8">
                        <div className="flex flex-col">
                            {/* Name */}
                            <div className="flex flex-row w-full text-[#494949] text-base font-bold">
                                <div className="inline-flex px-2 justify-center items-center gap-4 shrink-0">
                                    Your Name
                                </div>
                                <div className="inline-flex shrink-0 bg-[#3A57FF] text-[#FAFAFA] text-base font-[400] h-7">
                                    (Required*)
                                </div>
                            </div>
                            <div className="flex flex-col">
                                <input
                                    type="text"
                                    className="w-[334px] h-[52px] px-4 py-4 gap-4 rounded-[24px] bg-[rgba(0,0,0,0.15)] mt-[18px] mb-[16px]
                    placeholder:italic placeholder:text-[#888]
                    focus:outline-none focus:ring-2 focus:ring-[#3A57FF]"
                                    placeholder='Enter your name'
                                    value={formData.name}
                                    onChange={(e) =>
                                        setFormData({ ...formData, name: e.target.value })
                                    }
                                />
                            </div>

                            {/* Email */}
                            <div className="flex flex-row w-full text-[#494949] text-base font-bold">
                                <div className="inline-flex px-2 justify-center items-center gap-4 shrink-0">
                                    Your Email
                                </div>
                                <div className="inline-flex shrink-0 bg-[#3A57FF] text-[#FAFAFA] text-base font-[400] h-7">
                                    (Required*)
                                </div>
                            </div>
                            <div className="flex flex-col">
                                <input
                                    type="email"
                                    className="w-[334px] h-[52px] px-4 py-4 gap-4 rounded-[24px] bg-[rgba(0,0,0,0.15)] mt-[18px] mb-[32px]
                    placeholder:italic placeholder:text-[#888]
                    focus:outline-none focus:ring-2 focus:ring-[#3A57FF]"
                                    placeholder="Enter your email address"
                                    value={formData.email}
                                    onChange={(e) =>
                                        setFormData({ ...formData, email: e.target.value })
                                    }
                                />
                            </div>

                            {/* Captcha */}
                            <div className="flex flex-col ms-1">
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={RECAPTCHA_KEY}
                                    onChange={(token) => setCaptchaToken(token)}
                                    onExpired={() => setCaptchaToken(null)}
                                    size="normal"
                                />
                            </div>
                        </div>

                        {/* Message box */}
                        <div className="flex flex-col justify-start w-[453px]">
                            <div className="flex flex-row w-full text-[#494949] text-base font-bold">
                                <div className="inline-flex px-2 justify-center items-center gap-4 shrink-0">
                                    Your Message
                                </div>
                                <div className="inline-flex shrink-0 bg-[#3A57FF] text-[#FAFAFA] text-base font-[400] pb-5 h-7">
                                    (Required*)
                                </div>
                            </div>

                            <div className="flex flex-1">
                                <textarea
                                    className="w-full rounded-[24px] bg-[rgba(0,0,0,0.15)] mt-[18px] mb-[32px] px-4 py-4 placeholder:italic placeholder:text-[#888] resize-none focus:outline-none focus:ring-2 focus:ring-[#3A57FF]"
                                    placeholder="Enter your message"
                                    value={formData.message}
                                    onChange={(e) =>
                                        setFormData({ ...formData, message: e.target.value })
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    {/* Submit Button with animation */}
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        animate="visible"
                        custom={2}
                        className='flex w-full item-center justify-center mt-5'
                    >
                        <button
                            onClick={handleSubmtit}
                            disabled={loading}
                            className="w-[343px] inline-flex h-[53px] px-8 py-4 justify-center items-center gap-2 flex-shrink-0 rounded-[16px] bg-[#3A57FF] text-[17px] font-normal leading-[normal] text-[#FAFAFA]"
                        >
                            <span className="flex items-center justify-center mb-1">{loading ? "Sending..." : "Submit"}&nbsp;</span>
                            <img
                                src={send}
                                alt="Send"
                                className="h-[24px]"
                            />
                        </button>
                    </motion.div>
                </motion.div>
            </div>
        </>
    );
};

export default ContactForm;
